import {
  type CostBookingWindowView,
  type CostValuesView,
  type RawCostValue,
  WEEKDAY_COUNT,
} from 'lumen/utils/net-revenue/cost/types';

/**
 * The only place the backend's flat `cost_values` shape is mapped to the UI's
 * `CostValuesView`, so a BE field rename only touches this file and its test.
 */
function bookingWindowKey(entry: RawCostValue): string {
  return `${entry.leadtime_start}|${entry.leadtime_end}`;
}

export function costValuesToView(raw: RawCostValue[]): CostValuesView {
  const isAdvanced = raw.some((entry) => entry.weekday !== null);

  if (!isAdvanced) {
    return {
      bookingWindows: [],
      value: raw[0]?.value == null ? null : Number(raw[0].value),
    };
  }

  // Keyed by lead-time range, preserving encounter order.
  const bookingWindowsByKey = new Map<string, CostBookingWindowView>();
  for (const entry of raw) {
    // `weekday` is ISO-8601 (1=Mon…7=Sun) on the wire, but the view stores a
    // 0-based index (0=Mon…6=Sun) — offset by one in both directions.
    if (
      entry.weekday === null ||
      entry.weekday < 1 ||
      entry.weekday > WEEKDAY_COUNT
    ) {
      continue;
    }

    const key = bookingWindowKey(entry);
    let bookingWindow = bookingWindowsByKey.get(key);
    if (!bookingWindow) {
      bookingWindow = {
        leadtimeEnd: entry.leadtime_end,
        leadtimeStart: entry.leadtime_start,
        values: Array<number | null>(WEEKDAY_COUNT).fill(null),
      };
      bookingWindowsByKey.set(key, bookingWindow);
    }
    bookingWindow.values[entry.weekday - 1] = Number(entry.value);
  }

  return {
    bookingWindows: [...bookingWindowsByKey.values()],
    value: null,
  };
}

export function viewToCostValues(view: CostValuesView): RawCostValue[] {
  if (view.bookingWindows.length > 0) {
    return view.bookingWindows.flatMap((bookingWindow) =>
      bookingWindow.values.reduce<RawCostValue[]>((entries, value, weekday) => {
        if (value !== null) {
          entries.push({
            leadtime_end: bookingWindow.leadtimeEnd,
            leadtime_start: bookingWindow.leadtimeStart,
            value,
            // View index is 0-based (0=Mon…6=Sun); the wire format is
            // ISO-8601 (1=Mon…7=Sun).
            weekday: weekday + 1,
          });
        }
        return entries;
      }, []),
    );
  }

  if (view.value === null) {
    return [];
  }

  return [
    {
      leadtime_end: null,
      leadtime_start: null,
      value: view.value,
      weekday: null,
    },
  ];
}
